import React, { useState, useEffect } from 'react';
import { MarketNavLink as NavLink } from './MarketLink';
import { Home, LayoutGrid as Grid, Play, ShoppingBag, User, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isRTL } from '../utils/translations';
import { liveAPI } from '../api/client';

const BottomNav = ({ cartCount = 0 }) => {
    const { isAuthenticated } = useAuth();
    const isAr = isRTL();

    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [activeBroadcastId, setActiveBroadcastId] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const checkLive = () => {
            liveAPI.getActiveRooms().then(rooms => {
                if (!isMounted) return;
                const broadcasts = (rooms || []).filter(r => r.status === 'ACTIVE' || r.type === 'BROADCAST' || r.type === 'LIVE_COMMERCE' || r.type === 'LIVE');
                if (broadcasts.length > 0) setActiveBroadcastId(broadcasts[0].id);
                else setActiveBroadcastId(null);
            }).catch(() => {});
        };
        checkLive();
        const interval = setInterval(checkLive, 30000);
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY > lastScrollY && currentScrollY > 50) {
                // Scrolling down
                setIsVisible(false);
            } else {
                // Scrolling up
                setIsVisible(true);
            }
            setLastScrollY(currentScrollY);
        };

        const onVideoScroll = (e) => {
            if (e.detail === 'down') setIsVisible(false);
            else if (e.detail === 'up') setIsVisible(true);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('video-scroll', onVideoScroll);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('video-scroll', onVideoScroll);
        };
    }, [lastScrollY]);

    const openMessages = (e) => {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('luxvono-chat:open', { detail: { tab: 'c2c' } }));
    };

    return (
        <nav className={`bottom-nav ${isVisible ? '' : 'nav-hidden'}`}>
            {/* 1. Home / الرئيسية */}
            <NavLink to="/" end className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}>
                <div className="bottom-nav-icon-wrap">
                    <Home size={22} />
                </div>
                <span className="bottom-nav-label">{isAr ? 'الرئيسية' : 'Home'}</span>
            </NavLink>

            {/* 2. Store Categories / أقسام المتجر */}
            <NavLink to="/categories" className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}>
                <div className="bottom-nav-icon-wrap">
                    <Grid size={22} />
                </div>
                <span className="bottom-nav-label">{isAr ? 'أقسام المتجر' : 'Store'}</span>
            </NavLink>

            {/* 3. Videos / فيديو */}
            <NavLink to="/videos" className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}>
                <div className="bottom-nav-icon-wrap" style={{ position: 'relative' }}>
                    <Play size={22} />
                    {activeBroadcastId && (
                        <span className="bottom-nav-live-dot" />
                    )}
                </div>
                <span className="bottom-nav-label">{isAr ? 'فيديو' : 'Videos'}</span>
            </NavLink>

            {/* 4. Messages / الرسائل */}
            <button 
                type="button" 
                onClick={openMessages} 
                className="bottom-nav-item bottom-nav-btn"
                aria-label={isAr ? 'الرسائل' : 'Messages'}
            >
                <div className="bottom-nav-icon-wrap">
                    <MessageSquare size={22} />
                </div>
                <span className="bottom-nav-label">{isAr ? 'الرسائل' : 'Messages'}</span>
            </button>

            {/* 5. Cart / السلة */}
            <NavLink to="/cart" id="mobile-cart-icon" className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}>
                <div className="bottom-nav-icon-wrap" style={{ position: 'relative' }}>
                    <ShoppingBag size={22} />
                    {cartCount > 0 && <span className="cart-badge-bottom">{cartCount}</span>}
                </div>
                <span className="bottom-nav-label">{isAr ? 'السلة' : 'Cart'}</span>
            </NavLink>

            {/* 6. Profile / حسابي */}
            <NavLink to={isAuthenticated ? "/account/profile" : "/login"} className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}>
                <div className="bottom-nav-icon-wrap">
                    <User size={22} />
                </div>
                <span className="bottom-nav-label">{isAr ? 'حسابي' : 'Account'}</span>
            </NavLink>
        </nav>
    );
};

export default BottomNav;
